import { EmptyState, Skeleton } from '@bloom/ui';
import { Lock } from 'lucide-react';
import { Navigate, Outlet, useLocation } from 'react-router';
import { Page } from '../components/page';
import { ADMIN } from '../copy/admin';
import { ADMIN_PERMISSIONS, loginPath, useSession } from '../lib/session';

function Placeholder() {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-canvas" aria-busy="true">
      <Skeleton className="h-2 w-40" />
    </div>
  );
}

/** Signed-in area. A signed-out visitor goes to sign-in and comes back afterwards. */
export function RequireAuth() {
  const { user } = useSession();
  const location = useLocation();
  if (user === undefined) return <Placeholder />;
  if (user === null) {
    return <Navigate to={loginPath(`${location.pathname}${location.search}`)} replace />;
  }
  return <Outlet />;
}

/** Sign-in for visitors; a signed-in user continues to where they were going. */
export function GuestOnly() {
  const { user } = useSession();
  const location = useLocation();
  if (user === undefined) return <Placeholder />;
  if (user) {
    const next = new URLSearchParams(location.search).get('next');
    return <Navigate to={next?.startsWith('/') && !next.startsWith('//') ? next : '/'} replace />;
  }
  return <Outlet />;
}

/**
 * Admin guard (UX only; the API authorizes every call with the same platform grants). The
 * platform permissions come from `GET /v1/authorization/me/platform-permissions`, exactly as
 * `apps/web` gates its platform pages. Without any moderation grant: «مش مسموح».
 */
export function RequireAdmin() {
  const session = useSession();
  const location = useLocation();
  if (session.user === undefined || session.platformLoading) return <Placeholder />;
  if (session.user === null) {
    return <Navigate to={loginPath(`${location.pathname}${location.search}`)} replace />;
  }
  if (!ADMIN_PERMISSIONS.some((permission) => session.canPlatform(permission))) {
    return (
      <Page>
        <EmptyState
          titleAs="h1"
          icon={<Lock className="size-5" />}
          title={ADMIN.forbiddenTitle}
          description={ADMIN.forbiddenBody}
        />
      </Page>
    );
  }
  return <Outlet />;
}
